// File: src/pages/Inventory/SupplierCatalogPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  Download,
  Loader,
  RefreshCcw,
  Save,
  Search,
  UploadCloud,
  Wand2,
  XCircle,
  ShieldCheck,
  X,
  Link2,
  Sparkles,
} from "lucide-react";
import api from "@/lib/axios";

const STORAGE_KEY = "supplier_catalog_import_session_v3";

const FIELD_DEFINITIONS = [
  { key: "barcode", label: "Barcode", required: false, aliases: ["item_barcode", "barcode", "ean", "upc"] },
  { key: "supplier_sku", label: "Supplier SKU", required: false, aliases: ["supplier_sku", "sku", "supplier_item_code"] },
  { key: "item_code", label: "Item Code", required: false, aliases: ["item_code", "stock_code", "internal_code"] },
  { key: "item_name", label: "Item Name", required: false, aliases: ["item_name", "name", "product_name"] },
  { key: "description", label: "Description", required: true, aliases: ["description", "item_description", "product_description"] },
  { key: "gallery", label: "Gallery", required: false, aliases: ["gallery", "image", "image_url", "photo_url"] },
  { key: "allergens", label: "Allergens", required: false, aliases: ["allergens", "allergen_text"] },
  { key: "ingredients", label: "Ingredients", required: false, aliases: ["ingredients", "ingredients_text"] },
  { key: "brand", label: "Brand", required: false, aliases: ["brand"] },
  { key: "pack_size", label: "Pack Size", required: false, aliases: ["pack_size", "pack"] },
  { key: "unit_size", label: "Unit Size", required: false, aliases: ["unit_size", "size"] },
  { key: "category", label: "Category", required: false, aliases: ["category"] },
  { key: "notes", label: "Notes", required: false, aliases: ["notes"] },
];

function asNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function shortText(v, max = 80) {
  const s = String(v || "").trim();
  if (!s) return "—";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function extractList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.catalog)) return data.catalog;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function normalizeCellValue(v) {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

function buildInitialMapping(headers) {
  const normalizedHeaders = headers.map((h) => ({
    raw: h,
    normalized: normalizeHeader(h),
  }));

  const mapping = {};
  FIELD_DEFINITIONS.forEach((field) => {
    const found = normalizedHeaders.find((h) => field.aliases.includes(h.normalized));
    mapping[field.key] = found ? found.raw : "";
  });
  return mapping;
}

function transformRowsWithMapping(rows, mapping) {
  return rows
    .map((raw) => {
      const out = {};
      FIELD_DEFINITIONS.forEach((field) => {
        const sourceHeader = mapping[field.key];
        out[field.key] = sourceHeader ? normalizeCellValue(raw?.[sourceHeader]) : "";
      });
      return out;
    })
    .filter(
      (row) =>
        row.barcode ||
        row.supplier_sku ||
        row.item_code ||
        row.item_name ||
        row.description
    );
}

function rowStatus(row) {
  if (row?.matched) return "matched";
  if (row?.is_meaningful) return "warning";
  return "unmatched";
}

function rowStatusLabel(row) {
  if (row?.matched) return "Matched";
  if (row?.is_meaningful) return "Needs review";
  return "Weak row";
}

function badgeClass(kind) {
  switch (kind) {
    case "matched":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "unmatched":
      return "bg-rose-100 text-rose-800 border-rose-200";
    case "warning":
      return "bg-amber-100 text-amber-800 border-amber-200";
    default:
      return "bg-blue-100 text-blue-800 border-blue-200";
  }
}

function confidenceTone(confidence) {
  const n = asNum(confidence);
  if (n === null) return "text-gray-500";
  if (n >= 0.9) return "text-emerald-700";
  if (n >= 0.7) return "text-cyan-700";
  if (n >= 0.4) return "text-amber-700";
  return "text-rose-700";
}

function deriveCatalogRowStatus(row) {
  if (
    row?.linked_item_id ||
    row?.approved_item_id ||
    row?.item_id ||
    row?.linked_stock_item_id
  ) {
    return "matched";
  }

  if (
    row?.description ||
    row?.item_name ||
    row?.supplier_sku ||
    row?.barcode ||
    row?.item_code
  ) {
    return "warning";
  }

  return "unmatched";
}

function deriveCatalogRowStatusLabel(row) {
  const status = deriveCatalogRowStatus(row);
  if (status === "matched") return "Linked";
  if (status === "warning") return "Needs review";
  return "Weak row";
}

function normalizeCatalogListRow(row) {
  const allergens = Array.isArray(row?.allergens)
    ? row.allergens
    : Array.isArray(row?.extracted_allergens)
      ? row.extracted_allergens
      : typeof row?.allergens === "string" && row.allergens.trim()
        ? row.allergens.split(/[,/|]/).map((v) => v.trim()).filter(Boolean)
        : [];

  return {
    catalog_item_id: row?.catalog_item_id ?? row?.id ?? row?.catalog_id ?? null,
    supplier_sku: row?.supplier_sku ?? "",
    barcode: row?.barcode ?? "",
    item_code: row?.item_code ?? "",
    item_name: row?.item_name ?? "",
    description: row?.description ?? "",
    brand: row?.brand ?? "",
    category: row?.category ?? "",
    uploaded_at: row?.imported_at ?? row?.created_at ?? row?.linked_at ?? "",
    linked_item_id:
      row?.linked_item_id ??
      row?.approved_item_id ??
      row?.item_id ??
      row?.linked_stock_item_id ??
      null,
    linked_item_code:
      row?.linked_item_code ??
      row?.approved_item_code ??
      row?.stock_item_code ??
      "",
    linked_item_description:
      row?.linked_item_description ??
      row?.approved_item_description ??
      row?.stock_item_description ??
      "",
    allergens,
    raw: row,
  };
}

function buildStatsFromRows(rows) {
  const total = rows.length;
  const linked = rows.filter((row) => !!row.linked_item_id).length;
  const needsReview = rows.filter(
    (row) =>
      !row.linked_item_id &&
      (row.description || row.item_name || row.supplier_sku || row.barcode || row.item_code)
  ).length;
  const withAllergens = rows.filter((row) => Array.isArray(row.allergens) && row.allergens.length > 0).length;

  return {
    total_items: total,
    linked_items: linked,
    unlinked_items: Math.max(total - linked, 0),
    needs_review: needsReview,
    allergen_items: withAllergens,
  };
}

function buildVendorParams(vendorId) {
  const n = Number(vendorId);
  if (vendorId === "" || vendorId === null || vendorId === undefined) return {};
  if (!Number.isFinite(n)) return {};
  return { vendor_id: n };
}

function SummaryStat({ label, value, tone = "text-white", subtext = "" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-sm">
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold ${tone}`}>{value}</div>
      {subtext ? <div className="mt-1 text-xs text-white/45">{subtext}</div> : null}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <div className="rounded-xl bg-cyan-400/10 border border-cyan-400/20 p-2">
          <Icon className="w-4 h-4 text-cyan-300" />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">{title}</div>
          {subtitle ? <div className="text-xs text-white/45">{subtitle}</div> : null}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ row }) {
  const status = rowStatus(row);
  const label = rowStatusLabel(row);

  return (
    <span className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 font-medium ${badgeClass(status)}`}>
      {status === "matched" ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : status === "warning" ? (
        <AlertTriangle className="w-3.5 h-3.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5" />
      )}
      {label}
    </span>
  );
}

function RegistryStatusPill({ row }) {
  const status = deriveCatalogRowStatus(row);
  const label = deriveCatalogRowStatusLabel(row);

  return (
    <span className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 font-medium ${badgeClass(status)}`}>
      {status === "matched" ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : status === "warning" ? (
        <AlertTriangle className="w-3.5 h-3.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5" />
      )}
      {label}
    </span>
  );
}

function AllergenBadges({ allergens }) {
  if (!Array.isArray(allergens) || allergens.length === 0) {
    return <div className="text-sm text-gray-500">No allergens extracted</div>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {allergens.map((name) => (
        <span
          key={name}
          className="text-[11px] px-2.5 py-1 rounded-full bg-violet-100 text-violet-800 border border-violet-200 font-medium"
        >
          {name}
        </span>
      ))}
    </div>
  );
}

function SuggestedMatchCard({ row }) {
  if (!row?.suggested_match) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="text-sm font-medium text-gray-700">No suggested match</div>
        <div className="text-xs text-gray-500 mt-1">Search manually and choose the correct stock item.</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm">
      <div className="font-semibold text-gray-900">
        {row.suggested_match.item_code || "—"} — {row.suggested_match.description || "—"}
      </div>
      <div className="mt-2 text-xs text-gray-600 space-y-1">
        <div>
          Method: <span className="font-semibold">{row.match_method || "—"}</span>
        </div>
        <div className={confidenceTone(row.match_confidence)}>
          Confidence: <span className="font-semibold">{row.match_confidence ?? "—"}</span>
        </div>
      </div>
    </div>
  );
}

function ApprovedMatchCard({ merged, expanded, onToggle }) {
  const approvedId = merged.approved_item_id === "" ? null : Number(merged.approved_item_id);

  return (
    <div className="space-y-2">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 shadow-sm">
        <div className="text-[11px] uppercase tracking-[0.16em] text-blue-700 font-semibold mb-2">
          Approved Match
        </div>

        {Number.isFinite(approvedId) ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
            <Link2 className="w-4 h-4" />
            item_id: {approvedId}
          </div>
        ) : (
          <div className="text-sm text-blue-800">No approved item selected</div>
        )}
      </div>

      <button
        onClick={onToggle}
        className="w-full px-3 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 inline-flex items-center justify-center gap-2 text-sm font-medium transition"
      >
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {expanded ? "Hide Review Panel" : "Review / Override"}
      </button>
    </div>
  );
}

function SearchPanel({
  row,
  merged,
  setRowPatch,
  stockSearch,
  setStockSearch,
  searchStockItems,
  stockSearchResults,
  stockSearchLoading,
}) {
  const rowIndex = row.row_index;
  const searchResults = stockSearchResults[rowIndex] || [];
  const loadingSearch = !!stockSearchLoading[rowIndex];

  return (
    <div className="rounded-3xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 lg:p-5 shadow-sm">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-1.5 font-semibold">
            Approved Item ID
          </div>
          <input
            type="number"
            min="1"
            value={merged.approved_item_id}
            onChange={(e) => setRowPatch(rowIndex, { approved_item_id: e.target.value })}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            placeholder="approved item_id"
          />
        </label>

        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-1.5 font-semibold">
            Match Method
          </div>
          <input
            value={merged.match_method}
            onChange={(e) => setRowPatch(rowIndex, { match_method: e.target.value })}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            placeholder="match method"
          />
        </label>

        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-1.5 font-semibold">
            Confidence
          </div>
          <input
            type="number"
            min="0"
            max="1"
            step="0.01"
            value={merged.match_confidence}
            onChange={(e) => setRowPatch(rowIndex, { match_confidence: e.target.value })}
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            placeholder="confidence"
          />
        </label>
      </div>

      <div className="space-y-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-1.5 font-semibold">
            Search Stock Items
          </div>

          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={stockSearch[rowIndex] || ""}
              onChange={(e) =>
                setStockSearch((prev) => ({
                  ...prev,
                  [rowIndex]: e.target.value,
                }))
              }
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
              placeholder="search by item code, barcode, description..."
            />

            <button
              onClick={() => searchStockItems(rowIndex)}
              disabled={loadingSearch}
              className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 font-medium"
            >
              {loadingSearch ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>
        </label>

        {searchResults.length > 0 && (
          <div className="border border-gray-200 rounded-2xl bg-white max-h-56 overflow-auto shadow-sm">
            {searchResults.map((item) => {
              const itemId = item.item_id ?? item.id;
              const itemCode = item.item_code || "—";
              const itemDesc = item.description || item.name || "—";
              const isSelected = Number(merged.approved_item_id) === Number(itemId);

              return (
                <button
                  key={`${rowIndex}-${itemId}`}
                  onClick={() =>
                    setRowPatch(rowIndex, {
                      approved_item_id: itemId,
                      match_method: "manual",
                    })
                  }
                  className={`w-full text-left px-3 py-3 border-b last:border-b-0 transition ${isSelected ? "bg-cyan-50" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {itemCode} — {itemDesc}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">item_id: {itemId}</div>
                    </div>

                    {isSelected ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200 font-semibold">
                        Selected
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-sm text-gray-700 font-medium">
          <input
            type="checkbox"
            checked={!!merged.apply_allergens}
            onChange={(e) => setRowPatch(rowIndex, { apply_allergens: e.target.checked })}
          />
          Apply extracted allergens on commit
        </label>
      </div>
    </div>
  );
}

function PreviewRow({
  row,
  merged,
  expanded,
  onToggle,
  setRowPatch,
  stockSearch,
  setStockSearch,
  searchStockItems,
  stockSearchResults,
  stockSearchLoading,
}) {
  return (
    <>
      <tr className="align-top border-t border-gray-200 hover:bg-gray-50/70 transition">
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold">{row.row_index + 1}</td>

        <td className="px-4 py-4 min-w-[220px]">
          <div className="font-bold text-gray-900 text-base">{row.normalized?.item_name || "—"}</div>
          <div className="text-xs text-gray-500 mt-2">
            SKU: {row.normalized?.supplier_sku || "—"} • Barcode: {row.normalized?.barcode || "—"}
          </div>
          <div className="text-sm text-gray-700 mt-2">{shortText(row.normalized?.description || "—", 95)}</div>
        </td>

        <td className="px-4 py-4 min-w-[230px]">
          <SuggestedMatchCard row={row} />
        </td>

        <td className="px-4 py-4 min-w-[200px]">
          <ApprovedMatchCard merged={merged} expanded={expanded} onToggle={onToggle} />
        </td>

        <td className="px-4 py-4 min-w-[180px]">
          <AllergenBadges allergens={row.extracted_allergens} />
        </td>

        <td className="px-4 py-4 min-w-[150px]">
          <div className="flex flex-col gap-2">
            <StatusPill row={row} />
            {!row.is_meaningful && (
              <div className="text-xs text-rose-600">This row has very little identifying content.</div>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="border-t border-gray-200 bg-gray-50/80">
          <td colSpan={6} className="px-4 py-5">
            <SearchPanel
              row={row}
              merged={merged}
              setRowPatch={setRowPatch}
              stockSearch={stockSearch}
              setStockSearch={setStockSearch}
              searchStockItems={searchStockItems}
              stockSearchResults={stockSearchResults}
              stockSearchLoading={stockSearchLoading}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function HeaderMappingModal({
  open,
  headers,
  mapping,
  setMapping,
  previewSampleRows,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-[#07101d] text-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-lg font-bold">Map Supplier File Columns</div>
            <div className="text-sm text-white/50">
              Confirm which uploaded columns should map into the supplier catalog import fields.
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 hover:bg-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-0">
          <div className="p-6 border-r border-white/10 overflow-auto max-h-[72vh]">
            <div className="space-y-4">
              {FIELD_DEFINITIONS.map((field) => (
                <div
                  key={field.key}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div>
                      <div className="text-sm font-semibold">{field.label}</div>
                      <div className="text-[11px] text-white/45">{field.key}</div>
                    </div>
                    {field.required ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-amber-500/20 border border-amber-400/20 text-amber-200">
                        Recommended
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/50">
                        Optional
                      </span>
                    )}
                  </div>

                  <select
                    value={mapping[field.key] || ""}
                    onChange={(e) =>
                      setMapping((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                  >
                    <option value="">— Not mapped —</option>
                    {headers.map((header) => (
                      <option key={`${field.key}-${header}`} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>

                  <div className="mt-2 text-[11px] text-white/35">
                    Suggested aliases: {field.aliases.join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 overflow-auto max-h-[72vh]">
            <div className="mb-4">
              <div className="text-sm font-semibold">Uploaded file preview</div>
              <div className="text-xs text-white/45 mt-1">
                First rows from the uploaded supplier file.
              </div>
            </div>

            {!previewSampleRows.length ? (
              <div className="text-sm text-white/50">No sample rows available.</div>
            ) : (
              <div className="overflow-auto rounded-2xl border border-white/10 bg-black/20">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/5 sticky top-0">
                    <tr>
                      {headers.map((header) => (
                        <th
                          key={header}
                          className="px-3 py-3 text-left text-xs font-semibold text-white/70 border-b border-white/10 whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewSampleRows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-white/5">
                        {headers.map((header) => (
                          <td
                            key={`${rowIndex}-${header}`}
                            className="px-3 py-2 text-xs text-white/75 align-top"
                          >
                            {String(row?.[header] ?? "") || "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 rounded-2xl border border-cyan-400/15 bg-cyan-400/5 p-4">
              <div className="text-sm font-semibold text-cyan-200">Best practice</div>
              <div className="mt-1 text-xs text-cyan-100/70 leading-relaxed">
                Map at least Description, and ideally Supplier SKU and Barcode where available.
                Allergens can be returned as comma-separated or slash-separated values.
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 bg-black/20">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white font-medium"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2.5 rounded-2xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold inline-flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            Confirm Mapping
          </button>
        </div>
      </div>
    </div>
  );
}

function MappedRowsPreview({ rows }) {
  if (!rows.length) return null;

  const columns = FIELD_DEFINITIONS.map((f) => f.key);
  const previewRows = rows.slice(0, 5);

  return (
    <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-cyan-400/15">
        <div className="text-sm font-semibold text-cyan-100">Mapped File Preview</div>
        <div className="text-xs text-cyan-100/60 mt-1">
          First {previewRows.length} normalized row(s) that will be sent to preview.
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[1100px] w-full text-xs">
          <thead className="bg-black/20">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-3 py-2 text-left font-semibold text-cyan-100/70 border-b border-cyan-400/10 whitespace-nowrap"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, idx) => (
              <tr key={idx} className="border-b border-cyan-400/10">
                {columns.map((column) => (
                  <td
                    key={`${idx}-${column}`}
                    className="px-3 py-2 text-white/80 align-top"
                  >
                    {shortText(row?.[column] || "—", 60)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImportPanel({
  vendorId,
  setVendorId,
  sourceFileName,
  setSourceFileName,
  uploadedFileName,
  uploadRows,
  uploadError,
  handleFileUpload,
  runPreview,
  runCommit,
  previewLoading,
  commitLoading,
  previewData,
  error,
}) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-5 shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
      <SectionTitle
        icon={UploadCloud}
        title="Import Supplier File"
        subtitle="Upload the completed supplier Excel or CSV file, then review and commit."
      />

      <div className="space-y-4">
        <label className="block">
          <div className="text-xs text-white/70 mb-1.5">Vendor ID (optional)</div>
          <input
            type="number"
            min="1"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            placeholder="Leave blank to work across all vendors"
            className="w-full bg-black/35 border border-white/10 rounded-2xl px-3 py-2.5 text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
          />
        </label>

        <label className="block">
          <div className="text-xs text-white/70 mb-1.5">Source File Name</div>
          <input
            value={sourceFileName}
            onChange={(e) => setSourceFileName(e.target.value)}
            className="w-full bg-black/35 border border-white/10 rounded-2xl px-3 py-2.5 text-white placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
          />
        </label>

        <div className="rounded-2xl border border-dashed border-cyan-400/30 bg-black/20 p-4">
          <div className="flex flex-col gap-3">
            <div>
              <div className="text-sm font-semibold text-white">Upload Supplier File</div>
              <div className="text-xs text-white/50 mt-1">Accepted formats: .xlsx, .xls, .csv</div>
            </div>

            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="block w-full text-sm text-white file:mr-4 file:rounded-xl file:border-0 file:bg-cyan-600 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-cyan-700"
            />

            {uploadedFileName ? (
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
                Uploaded: <span className="font-semibold">{uploadedFileName}</span>
                <div className="text-xs text-emerald-100/70 mt-1">Ready rows: {uploadRows.length}</div>
              </div>
            ) : null}

            {uploadError ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                {uploadError}
              </div>
            ) : null}
          </div>
        </div>

        <MappedRowsPreview rows={uploadRows} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={runPreview}
            disabled={previewLoading || !uploadRows.length}
            className="px-4 py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-95 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 font-semibold shadow-lg"
          >
            {previewLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            Preview Matches
          </button>

          <button
            onClick={runCommit}
            disabled={commitLoading || !previewData.length}
            className="px-4 py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 hover:opacity-95 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 font-semibold shadow-lg"
          >
            {commitLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Commit Import
          </button>
        </div>

        {error ? (
          <div className="text-xs rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-200 px-3 py-3 whitespace-pre-wrap">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SummaryCard({ stats }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur-xl">
      <SectionTitle
        icon={ShieldCheck}
        title="Catalog Summary"
        subtitle="Current supplier catalog totals available in this workspace."
      />

      <div className="grid grid-cols-2 gap-3">
        <SummaryStat
          label="Catalog Items"
          value={stats?.total_items ?? 0}
          subtext="Saved rows shown below"
        />
        <SummaryStat
          label="Linked"
          value={stats?.linked_items ?? 0}
          tone="text-emerald-300"
          subtext="Connected to stock items"
        />
        <SummaryStat
          label="Needs Review"
          value={stats?.needs_review ?? 0}
          tone="text-amber-300"
          subtext="Still requires attention"
        />
        <SummaryStat
          label="With Allergens"
          value={stats?.allergen_items ?? 0}
          tone="text-violet-300"
          subtext="Allergen data present"
        />
      </div>
    </div>
  );
}

function CommitSuccessCard({ commitResult }) {
  if (!commitResult) return null;

  return (
    <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-5">
      <div className="font-semibold text-emerald-300 mb-3 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" />
        Import Completed Successfully
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-emerald-400/15 bg-black/20 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-200/70">Imported</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{commitResult.inserted_count ?? 0}</div>
        </div>

        <div className="rounded-2xl border border-emerald-400/15 bg-black/20 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-200/70">Linked</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{commitResult.linked_count ?? 0}</div>
        </div>

        <div className="rounded-2xl border border-emerald-400/15 bg-black/20 px-4 py-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-200/70">Allergens Updated</div>
          <div className="mt-1 text-2xl font-extrabold text-white">{commitResult.allergen_update_count ?? 0}</div>
        </div>
      </div>
    </div>
  );
}

function ImportReviewPanel({
  previewData,
  setRowPatch,
  expandedRows,
  toggleExpanded,
  getMergedRow,
  stockSearch,
  setStockSearch,
  searchStockItems,
  stockSearchResults,
  stockSearchLoading,
}) {
  if (!previewData.length) return null;

  const matchedCount = previewData.filter((r) => !!r.matched).length;
  const reviewCount = previewData.filter((r) => !r.matched && !!r.is_meaningful).length;
  const weakCount = previewData.filter((r) => !r.matched && !r.is_meaningful).length;

  return (
    <div className="rounded-3xl border border-white/10 bg-white shadow-[0_20px_80px_rgba(0,0,0,0.35)] overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap bg-gradient-to-r from-gray-50 to-white">
        <div>
          <div className="text-base font-bold text-gray-900">Import Review</div>
          <div className="text-xs text-gray-500 mt-1">
            Review suggested matches, approve the correct stock item, then commit safely.
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2.5 py-1 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-200 font-semibold">
            Matched: {matchedCount}
          </span>
          <span className="text-[11px] px-2.5 py-1 rounded-full border bg-amber-100 text-amber-800 border-amber-200 font-semibold">
            Needs attention: {reviewCount + weakCount}
          </span>
          <span className="text-xs text-gray-500">{previewData.length} row(s)</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-gray-900">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-4 text-left font-bold">#</th>
              <th className="px-4 py-4 text-left font-bold">Catalog Item</th>
              <th className="px-4 py-4 text-left font-bold">Suggested Match</th>
              <th className="px-4 py-4 text-left font-bold">Approved Match</th>
              <th className="px-4 py-4 text-left font-bold">Allergens</th>
              <th className="px-4 py-4 text-left font-bold">Status</th>
            </tr>
          </thead>

          <tbody>
            {previewData.map((row) => {
              const merged = getMergedRow(row);
              const expanded = !!expandedRows[row.row_index];

              return (
                <PreviewRow
                  key={row.row_index}
                  row={row}
                  merged={merged}
                  expanded={expanded}
                  onToggle={() => toggleExpanded(row.row_index)}
                  setRowPatch={setRowPatch}
                  stockSearch={stockSearch}
                  setStockSearch={setStockSearch}
                  searchStockItems={searchStockItems}
                  stockSearchResults={stockSearchResults}
                  stockSearchLoading={stockSearchLoading}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CatalogRegistryTable({
  rows,
  loading,
  error,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
}) {
  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    return rows.filter((row) => {
      const status = deriveCatalogRowStatus(row);

      if (statusFilter !== "all" && status !== statusFilter) return false;

      if (!q) return true;

      const hay = [
        row.supplier_sku,
        row.barcode,
        row.item_code,
        row.item_name,
        row.description,
        row.brand,
        row.category,
        row.linked_item_code,
        row.linked_item_description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, search, statusFilter]);

  return (
    <div className="rounded-3xl border border-white/10 bg-white shadow-[0_20px_80px_rgba(0,0,0,0.35)] overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-base font-bold text-gray-900">Supplier Catalog Registry</div>
            <div className="text-xs text-gray-500 mt-1">
              Imported supplier catalog records available in this workspace.
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search catalog..."
                className="w-[220px] pl-9 pr-3 py-2 rounded-xl border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            >
              <option value="all">All statuses</option>
              <option value="matched">Linked</option>
              <option value="warning">Needs review</option>
              <option value="unmatched">Weak rows</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="px-6 py-16 text-center text-gray-500">
          <Loader className="w-8 h-8 mx-auto mb-3 animate-spin text-gray-400" />
          Loading supplier catalog...
        </div>
      ) : error ? (
        <div className="px-6 py-10">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-rose-700 text-sm">
            {error}
          </div>
        </div>
      ) : !filteredRows.length ? (
        <div className="px-6 py-16 text-center text-gray-500">
          <div className="mx-auto w-16 h-16 rounded-3xl bg-gray-100 border border-gray-200 flex items-center justify-center mb-4">
            <Database className="w-8 h-8 text-gray-400" />
          </div>
          <div className="text-lg font-semibold text-gray-700">No catalog records found</div>
          <div className="text-sm text-gray-500 mt-1">
            Import a supplier file to start building the supplier catalog registry.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-sm text-gray-900">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-4 text-left font-bold">Catalog Item</th>
                <th className="px-4 py-4 text-left font-bold">Supplier Ref</th>
                <th className="px-4 py-4 text-left font-bold">Description</th>
                <th className="px-4 py-4 text-left font-bold">Linked Stock Item</th>
                <th className="px-4 py-4 text-left font-bold">Allergens</th>
                <th className="px-4 py-4 text-left font-bold">Status</th>
                <th className="px-4 py-4 text-left font-bold">Imported</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.catalog_item_id || `${row.supplier_sku}-${row.barcode}-${row.item_code}`} className="border-t border-gray-200 hover:bg-gray-50/70 transition">
                  <td className="px-4 py-4 align-top min-w-[220px]">
                    <div className="font-semibold text-gray-900">
                      {row.item_name || row.item_code || row.supplier_sku || "—"}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Brand: {row.brand || "—"} • Category: {row.category || "—"}
                    </div>
                  </td>

                  <td className="px-4 py-4 align-top min-w-[180px]">
                    <div className="text-sm font-medium text-gray-900">SKU: {row.supplier_sku || "—"}</div>
                    <div className="text-xs text-gray-500 mt-1">Barcode: {row.barcode || "—"}</div>
                    <div className="text-xs text-gray-500 mt-1">Item Code: {row.item_code || "—"}</div>
                  </td>

                  <td className="px-4 py-4 align-top min-w-[260px]">
                    <div className="text-sm text-gray-800">{shortText(row.description || "—", 120)}</div>
                  </td>

                  <td className="px-4 py-4 align-top min-w-[230px]">
                    {row.linked_item_id ? (
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <div className="font-semibold text-gray-900">
                          {row.linked_item_code || "—"} — {row.linked_item_description || "—"}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">item_id: {row.linked_item_id}</div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                        Not linked yet
                      </div>
                    )}
                  </td>

                  <td className="px-4 py-4 align-top min-w-[180px]">
                    <AllergenBadges allergens={row.allergens} />
                  </td>

                  <td className="px-4 py-4 align-top min-w-[140px]">
                    <RegistryStatusPill row={row} />
                  </td>

                  <td className="px-4 py-4 align-top min-w-[140px]">
                    <div className="text-sm text-gray-800">
                      {row.uploaded_at ? new Date(row.uploaded_at).toLocaleString() : "—"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SupplierCatalogPage() {
  const [vendorId, setVendorId] = useState("");
  const [sourceFileName, setSourceFileName] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadRows, setUploadRows] = useState([]);
  const [uploadError, setUploadError] = useState("");

  const [rawUploadedRows, setRawUploadedRows] = useState([]);
  const [uploadedHeaders, setUploadedHeaders] = useState([]);
  const [headerMapping, setHeaderMapping] = useState({});
  const [mappingModalOpen, setMappingModalOpen] = useState(false);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [commitResult, setCommitResult] = useState(null);

  const [rowEdits, setRowEdits] = useState({});
  const [stockSearch, setStockSearch] = useState({});
  const [stockSearchResults, setStockSearchResults] = useState({});
  const [stockSearchLoading, setStockSearchLoading] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const [catalogRows, setCatalogRows] = useState([]);
  const [catalogStats, setCatalogStats] = useState({
    total_items: 0,
    linked_items: 0,
    unlinked_items: 0,
    needs_review: 0,
    allergen_items: 0,
  });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogStatusFilter, setCatalogStatusFilter] = useState("all");
  const [showImportSection, setShowImportSection] = useState(true);

  const previewSampleRows = useMemo(() => rawUploadedRows.slice(0, 5), [rawUploadedRows]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object") return;

      setVendorId(saved.vendorId || "");
      setSourceFileName(saved.sourceFileName || "");
      setUploadedFileName(saved.uploadedFileName || "");
      setUploadRows(Array.isArray(saved.uploadRows) ? saved.uploadRows : []);
      setRawUploadedRows(Array.isArray(saved.rawUploadedRows) ? saved.rawUploadedRows : []);
      setUploadedHeaders(Array.isArray(saved.uploadedHeaders) ? saved.uploadedHeaders : []);
      setHeaderMapping(saved.headerMapping && typeof saved.headerMapping === "object" ? saved.headerMapping : {});
    } catch (e) {
      console.error("Failed to restore supplier catalog session:", e);
    }
  }, []);

  useEffect(() => {
    try {
      const payload = {
        vendorId,
        sourceFileName,
        uploadedFileName,
        uploadRows,
        rawUploadedRows,
        uploadedHeaders,
        headerMapping,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error("Failed to persist supplier catalog session:", e);
    }
  }, [vendorId, sourceFileName, uploadedFileName, uploadRows, rawUploadedRows, uploadedHeaders, headerMapping]);

  useEffect(() => {
    loadCatalogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const setRowPatch = (rowIndex, patch) => {
    setRowEdits((prev) => ({
      ...prev,
      [rowIndex]: {
        ...(prev[rowIndex] || {}),
        ...patch,
      },
    }));
  };

  const getMergedRow = (row) => {
    const edit = rowEdits[row.row_index] || {};
    return {
      ...row,
      approved_item_id:
        edit.approved_item_id !== undefined ? edit.approved_item_id : row?.suggested_match?.item_id ?? "",
      match_method:
        edit.match_method !== undefined
          ? edit.match_method
          : row.match_method || (row?.suggested_match?.item_id ? "manual" : ""),
      match_confidence:
        edit.match_confidence !== undefined ? edit.match_confidence : row.match_confidence ?? "",
      notes: edit.notes !== undefined ? edit.notes : "",
      apply_allergens: edit.apply_allergens !== undefined ? !!edit.apply_allergens : true,
    };
  };

  const toggleExpanded = (rowIndex) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const loadCatalogData = async () => {
    setCatalogLoading(true);
    setCatalogError("");

    try {
      const vendorParams = buildVendorParams(vendorId);

      const listPromise = api.get("/inventory/supplier-catalog/list", {
        params: {
          ...vendorParams,
          limit: 1000,
        },
      });

      const statsPromise = api
        .get("/inventory/supplier-catalog/stats", {
          params: vendorParams,
        })
        .catch(() => null);

      const [listRes, statsRes] = await Promise.all([listPromise, statsPromise]);

      const normalizedRows = extractList(listRes?.data).map(normalizeCatalogListRow);
      setCatalogRows(normalizedRows);

      if (statsRes?.data && typeof statsRes.data === "object") {
        setCatalogStats({
          total_items: statsRes.data.total_items ?? normalizedRows.length,
          linked_items: statsRes.data.linked_items ?? normalizedRows.filter((r) => !!r.linked_item_id).length,
          unlinked_items:
            statsRes.data.unlinked_items ??
            Math.max(normalizedRows.length - normalizedRows.filter((r) => !!r.linked_item_id).length, 0),
          needs_review:
            statsRes.data.needs_review ??
            normalizedRows.filter((r) => deriveCatalogRowStatus(r) === "warning").length,
          allergen_items:
            statsRes.data.allergen_items ??
            normalizedRows.filter((r) => Array.isArray(r.allergens) && r.allergens.length > 0).length,
        });
      } else {
        setCatalogStats(buildStatsFromRows(normalizedRows));
      }
    } catch (e) {
      console.error("Failed to load supplier catalog:", e);
      setCatalogRows([]);
      setCatalogStats(buildStatsFromRows([]));
      setCatalogError(
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        "Failed to load supplier catalog records."
      );
    } finally {
      setCatalogLoading(false);
    }
  };

  const downloadSupplierTemplate = () => {
    const templateRows = [
      {
        item_barcode: "",
        supplier_sku: "",
        item_code: "",
        description: "",
        gallery: "",
        allergens: "",
        ingredients: "",
        brand: "",
        pack_size: "",
        unit_size: "",
        category: "",
        notes: "",
      },
      {
        item_barcode: "1234567890123",
        supplier_sku: "74850",
        item_code: "MILK-PORTION-10ML",
        description: "Milk Stick SS 10Ml",
        gallery: "https://example.com/images/milk-stick-10ml.jpg",
        allergens: "Milk",
        ingredients: "Milk",
        brand: "Example Dairy",
        pack_size: "100 x 10ml",
        unit_size: "10ml",
        category: "Dairy",
        notes: "Example completed row",
      },
      {
        item_barcode: "2345678901234",
        supplier_sku: "FRU-001",
        item_code: "BANANA-FRESH",
        description: "Fresh Bananas",
        gallery: "https://example.com/images/fresh-bananas.jpg",
        allergens: "",
        ingredients: "Banana",
        brand: "Fresh Farm",
        pack_size: "1 case",
        unit_size: "1kg",
        category: "Fruit",
        notes: "",
      },
    ];

    const instructionRows = [
      { field: "item_barcode", requirement: "Recommended", guidance: "Supplier barcode / EAN / UPC if available." },
      { field: "supplier_sku", requirement: "Recommended", guidance: "Supplier's own SKU / item code." },
      { field: "item_code", requirement: "Optional", guidance: "Your internal stock code if known." },
      { field: "description", requirement: "Required", guidance: "Clear product description." },
      { field: "gallery", requirement: "Optional", guidance: "Image URL or file reference." },
      { field: "allergens", requirement: "Recommended", guidance: "Examples: Milk, Soy or Milk / Soy / Gluten." },
      { field: "ingredients", requirement: "Optional", guidance: "Ingredient list if supplier can provide it." },
      { field: "brand", requirement: "Optional", guidance: "Product brand." },
      { field: "pack_size", requirement: "Optional", guidance: "Examples: 100 x 10ml, 1 case, 6 x 2L." },
      { field: "unit_size", requirement: "Optional", guidance: "Examples: 10ml, 1kg, 500g." },
      { field: "category", requirement: "Optional", guidance: "Product category." },
      { field: "notes", requirement: "Optional", guidance: "Any additional supplier notes." },
    ];

    const wb = XLSX.utils.book_new();
    const templateWs = XLSX.utils.json_to_sheet(templateRows);
    const instructionsWs = XLSX.utils.json_to_sheet(instructionRows);

    templateWs["!cols"] = [
      { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 32 },
      { wch: 38 }, { wch: 24 }, { wch: 30 }, { wch: 18 },
      { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 24 },
    ];

    instructionsWs["!cols"] = [{ wch: 18 }, { wch: 16 }, { wch: 60 }];

    XLSX.utils.book_append_sheet(wb, templateWs, "Template");
    XLSX.utils.book_append_sheet(wb, instructionsWs, "Instructions");
    XLSX.writeFile(wb, "Supplier_Catalog_Template.xlsx");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError("");
    setError("");
    setCommitResult(null);

    try {
      const lower = file.name.toLowerCase();
      let rows = [];

      if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
      } else if (lower.endsWith(".csv")) {
        const text = await file.text();
        const workbook = XLSX.read(text, { type: "string" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
      } else {
        throw new Error("Please upload a valid .xlsx, .xls, or .csv file.");
      }

      if (!rows.length) {
        throw new Error("The uploaded file is empty.");
      }

      const headers = Object.keys(rows[0] || {});
      if (!headers.length) {
        throw new Error("No headers were found in the uploaded file.");
      }

      const initialMapping = buildInitialMapping(headers);

      setRawUploadedRows(rows);
      setUploadedHeaders(headers);
      setHeaderMapping(initialMapping);
      setUploadedFileName(file.name);
      setSourceFileName(file.name);
      setUploadRows([]);
      setPreviewData([]);
      setCommitResult(null);
      setRowEdits({});
      setExpandedRows({});
      setMappingModalOpen(true);
      setShowImportSection(true);
    } catch (err) {
      console.error("Upload parse failed:", err);
      setRawUploadedRows([]);
      setUploadedHeaders([]);
      setHeaderMapping({});
      setUploadRows([]);
      setUploadedFileName("");
      setUploadError(err?.message || "Failed to read uploaded file.");
    } finally {
      event.target.value = "";
    }
  };

  const confirmHeaderMapping = () => {
    const cleaned = transformRowsWithMapping(rawUploadedRows, headerMapping);

    if (!cleaned.length) {
      setUploadError("No usable rows were found after applying the selected header mapping.");
      return;
    }

    setUploadRows(cleaned);
    setUploadError("");
    setMappingModalOpen(false);
  };

  const runPreview = async () => {
    setError("");
    setCommitResult(null);

    if (!uploadRows.length) {
      setError("Please upload a supplier file first.");
      return;
    }

    const vId = vendorId === "" ? null : Number(vendorId);
    if (vId !== null && !Number.isFinite(vId)) {
      setError("Vendor ID must be a valid number.");
      return;
    }

    setPreviewLoading(true);
    try {
      const payload = {
        source_file_name: sourceFileName || uploadedFileName || null,
        rows: uploadRows,
      };

      if (vId !== null) {
        payload.vendor_id = vId;
      }

      const { data } = await api.post("/inventory/supplier-catalog/preview", payload);

      const list = Array.isArray(data?.preview) ? data.preview : [];
      setPreviewData(list);

      const seeded = {};
      const expandedSeed = {};

      list.forEach((row) => {
        seeded[row.row_index] = {
          approved_item_id: row?.suggested_match?.item_id ?? "",
          match_method: row.match_method || (row?.suggested_match?.item_id ? "manual" : ""),
          match_confidence: row.match_confidence ?? "",
          notes: "",
          apply_allergens: true,
        };

        if (!row?.matched) expandedSeed[row.row_index] = true;
      });

      setRowEdits(seeded);
      setExpandedRows(expandedSeed);
    } catch (e) {
      console.error("Supplier catalog preview failed:", e);
      setPreviewData([]);
      setError(e?.response?.data?.message || e?.response?.data?.error || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runCommit = async () => {
    setError("");
    setCommitResult(null);

    if (!previewData.length) {
      setError("Run preview first.");
      return;
    }

    const vId = vendorId === "" ? null : Number(vendorId);
    if (vId !== null && !Number.isFinite(vId)) {
      setError("Vendor ID must be a valid number.");
      return;
    }

    const rows = previewData.map((row) => {
      const merged = getMergedRow(row);
      const approved = merged.approved_item_id === "" ? null : Number(merged.approved_item_id);
      const confidence =
        merged.match_confidence === "" || merged.match_confidence == null
          ? null
          : Number(merged.match_confidence);

      return {
        normalized: row.normalized,
        approved_item_id: Number.isFinite(approved) ? approved : null,
        match_method: merged.match_method || null,
        match_confidence: Number.isFinite(confidence) ? confidence : null,
        notes: merged.notes || null,
        apply_allergens: !!merged.apply_allergens,
      };
    });

    setCommitLoading(true);
    try {
      const payload = {
        source_file_name: sourceFileName || uploadedFileName || null,
        rows,
      };

      if (vId !== null) {
        payload.vendor_id = vId;
      }

      const { data } = await api.post("/inventory/supplier-catalog/commit", payload);

      setCommitResult(data || {});
      setPreviewData([]);
      setRowEdits({});
      setExpandedRows({});
      await loadCatalogData();
    } catch (e) {
      console.error("Supplier catalog commit failed:", e);
      setError(e?.response?.data?.message || e?.response?.data?.error || "Commit failed.");
    } finally {
      setCommitLoading(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await loadCatalogData();
    } finally {
      setRefreshing(false);
    }
  };

  const searchStockItems = async (rowIndex) => {
    const q = String(stockSearch[rowIndex] || "").trim();
    if (!q) return;

    setStockSearchLoading((prev) => ({ ...prev, [rowIndex]: true }));
    try {
      const { data } = await api.get("/inventory/stock-items", {
        params: { q, limit: 25 },
      });

      setStockSearchResults((prev) => ({
        ...prev,
        [rowIndex]: extractList(data),
      }));
    } catch (e) {
      console.error("Stock search failed:", e);
      setStockSearchResults((prev) => ({ ...prev, [rowIndex]: [] }));
    } finally {
      setStockSearchLoading((prev) => ({ ...prev, [rowIndex]: false }));
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-10 xl:px-12 py-8 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.08),transparent_22%),radial-gradient(circle_at_top_right,rgba(168,85,247,0.08),transparent_25%),linear-gradient(135deg,#030712_0%,#0b1120_45%,#030712_100%)] text-white">
      <div className="max-w-[1800px] mx-auto">
        <HeaderMappingModal
          open={mappingModalOpen}
          headers={uploadedHeaders}
          mapping={headerMapping}
          setMapping={setHeaderMapping}
          previewSampleRows={previewSampleRows}
          onClose={() => setMappingModalOpen(false)}
          onConfirm={confirmHeaderMapping}
        />

        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs text-cyan-200 mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Supplier Catalog Management
            </div>

            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3">
              <div className="rounded-2xl bg-fuchsia-500/10 border border-fuchsia-400/20 p-2.5">
                <Database className="w-7 h-7 text-fuchsia-300" />
              </div>
              Supplier Catalog
            </h1>

            <p className="mt-3 text-sm md:text-base text-white/60 max-w-3xl">
              Manage imported supplier catalog records, review link coverage, and upload new supplier returns from one page.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={downloadSupplierTemplate}
              className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2.5 rounded-2xl inline-flex items-center gap-2 text-white font-semibold shadow-lg"
            >
              <Download className="w-4 h-4" />
              Download Supplier Template
            </button>

            <button
              onClick={() => setShowImportSection((prev) => !prev)}
              className="bg-white/8 hover:bg-white/12 border border-white/10 px-4 py-2.5 rounded-2xl inline-flex items-center gap-2 font-semibold"
            >
              <UploadCloud className="w-4 h-4" />
              {showImportSection ? "Hide Import" : "New Import"}
            </button>

            <button
              onClick={refreshAll}
              disabled={refreshing || catalogLoading}
              className="bg-white/8 hover:bg-white/12 border border-white/10 px-4 py-2.5 rounded-2xl inline-flex items-center gap-2 disabled:opacity-50 font-semibold"
            >
              {refreshing ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-8">
          <SummaryCard stats={catalogStats} />
        </div>

        <div className="grid grid-cols-1 2xl:grid-cols-12 gap-6">
          {showImportSection ? (
            <div className="2xl:col-span-4 space-y-5">
              <ImportPanel
                vendorId={vendorId}
                setVendorId={setVendorId}
                sourceFileName={sourceFileName}
                setSourceFileName={setSourceFileName}
                uploadedFileName={uploadedFileName}
                uploadRows={uploadRows}
                uploadError={uploadError}
                handleFileUpload={handleFileUpload}
                runPreview={runPreview}
                runCommit={runCommit}
                previewLoading={previewLoading}
                commitLoading={commitLoading}
                previewData={previewData}
                error={error}
              />

              <CommitSuccessCard commitResult={commitResult} />
            </div>
          ) : null}

          <div className={showImportSection ? "2xl:col-span-8 space-y-6" : "col-span-1 space-y-6"}>
            <ImportReviewPanel
              previewData={previewData}
              setRowPatch={setRowPatch}
              expandedRows={expandedRows}
              toggleExpanded={toggleExpanded}
              getMergedRow={getMergedRow}
              stockSearch={stockSearch}
              setStockSearch={setStockSearch}
              searchStockItems={searchStockItems}
              stockSearchResults={stockSearchResults}
              stockSearchLoading={stockSearchLoading}
            />

            <CatalogRegistryTable
              rows={catalogRows}
              loading={catalogLoading}
              error={catalogError}
              search={catalogSearch}
              setSearch={setCatalogSearch}
              statusFilter={catalogStatusFilter}
              setStatusFilter={setCatalogStatusFilter}
            />
          </div>
        </div>
      </div>
    </div>
  );
}